import React, { useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import CustomLayout from './CustomLayout';
import DashboardModern from './Components/DashboardModern';
import Login from './Components/Login';
import ProtectedRoute from './Components/ProtectedRoute';
import { createAppTheme } from './theme';
import { ServerProvider } from "./Context/ServerContext";
import { LanguageProvider } from "./Context/LanguageContext";
import { ThemeModeProvider, useThemeMode } from "./Context/ThemeContext";
import { dispatchResumeConnections } from './utils/wsResume';
import { isAuthenticated } from './utils/cloudApi';
import { lazyPage, PageSlot, preloadPagesWhenIdle } from './utils/lazyPage';

/* Dashboard and login load with the app; other pages are separate chunks fetched on demand. */
const Monitoring = lazyPage(() => import('./Components/Monitoring'));
const AdcAnalytics = lazyPage(() => import('./Components/AdcAnalytics'));
const Log = lazyPage(() => import('./Components/Log'));
const InstallationMapView = lazyPage(() => import('./Components/InstallationMapView'));
const SettingsModern = lazyPage(() => import('./Components/SettingsModern'));
const SensorMonitoringReport = lazyPage(() => import('./Components/Report/SensorMonitoringReport'));
const EnvoDashboard = lazyPage(() => import('./Components/EnvironmentNewDash'));
const MonotringDrawing = lazyPage(() => import('./Components/monDashMulti'));
const AiModelBeta = lazyPage(() => import('./Components/AiModelBeta'));
const Signup = lazyPage(() => import('./signup'));

/** Most-used first. */
const PRELOAD_PAGES = [Monitoring, AdcAnalytics, Log, InstallationMapView, SettingsModern, SensorMonitoringReport, EnvoDashboard];

/** Fetches the other pages in the background once the signed-in app is idle. */
const PagePreloader = () => {
  useEffect(() => preloadPagesWhenIdle(PRELOAD_PAGES), []);
  return null;
};

/** Root URL: login when signed out, dashboard when signed in. */
const HomeRoute = () =>
  isAuthenticated() ? <Navigate to="/dashboard" replace /> : <Login />;

function getElectronIpc() {
  try {
    if (typeof window !== "undefined" && typeof window.require === "function") {
      return window.require("electron").ipcRenderer;
    }
  } catch {
    /* browser / factory web UI */
  }
  return null;
}

function AppContent() {
  const { mode } = useThemeMode();
  const muiTheme = useMemo(() => createAppTheme(mode), [mode]);

  useEffect(() => {
    let awayAt = 0;
    let onlineTimer = 0;
    const ZOMBIE_MS = 45_000;

    const markAway = () => {
      if (!awayAt) awayAt = Date.now();
    };

    const resumeIfNeeded = (force) => {
      dispatchResumeConnections({ force: Boolean(force) });
    };

    const onReturn = (alwaysForce) => {
      const awayMs = awayAt ? Date.now() - awayAt : 0;
      awayAt = 0;
      resumeIfNeeded(alwaysForce || awayMs >= ZOMBIE_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        markAway();
        return;
      }
      if (document.visibilityState === "visible") onReturn(false);
    };

    const onPageShow = (event) => onReturn(Boolean(event?.persisted));

    const onOnline = () => {
      window.clearTimeout(onlineTimer);
      onlineTimer = window.setTimeout(() => resumeIfNeeded(true), 1500);
    };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("online", onOnline);

    const ipc = getElectronIpc();
    const onSystemResume = (_event, payload) => {
      const reason = String(payload?.reason || "");
      const force = reason === "os-resume" || reason === "unlock-screen";
      resumeIfNeeded(force);
    };
    if (ipc) ipc.on("haechie:system-resume", onSystemResume);

    return () => {
      window.clearTimeout(onlineTimer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("online", onOnline);
      if (ipc) ipc.removeListener("haechie:system-resume", onSystemResume);
    };
  }, []);

  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      <LanguageProvider>
      <ServerProvider>
        <Router>
          <Routes>
            {/* Login at site root: http://localhost:3000 */}
            <Route path="/" element={<HomeRoute />} />
            <Route path="/login" element={<Navigate to="/" replace />} />

            {/* Protected app routes */}
            <Route
              element={
                <ProtectedRoute>
                  <CustomLayout />
                  <PagePreloader />
                </ProtectedRoute>
              }
            >
              <Route path="/signup" element={<PageSlot><Signup /></PageSlot>} />
              <Route path="dashboard" element={<DashboardModern />} />
              <Route path="dashboard-classic" element={<PageSlot><MonotringDrawing /></PageSlot>} />
              <Route path="adc" element={<PageSlot><AdcAnalytics /></PageSlot>} />
              <Route path="Environ" element={<PageSlot><EnvoDashboard /></PageSlot>} />
              <Route path="monitoring" element={<PageSlot><Monitoring /></PageSlot>} />
              <Route path="installation-map" element={<PageSlot><InstallationMapView /></PageSlot>} />
              <Route path="ai-model-beta" element={<PageSlot><AiModelBeta /></PageSlot>} />
              <Route path="logs" element={<PageSlot><Log /></PageSlot>} />
              <Route path="report" element={<PageSlot><SensorMonitoringReport /></PageSlot>} />
              <Route path="settings" element={<PageSlot><SettingsModern /></PageSlot>} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </ServerProvider>
      </LanguageProvider>
    </ThemeProvider>
  );
}

function App() {
  return (
    <ThemeModeProvider>
      <AppContent />
    </ThemeModeProvider>
  );
}

export default App;